import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import { KeyboardArrowDown, Language } from '@mui/icons-material';
import { useLocalization } from '../contexts/LocalizationContext';

interface LanguageOption {
  code: 'en' | 'ja' | 'cn' | 'tc';
  name: string;
  nativeName: string;
}

const languages: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'tc', name: 'Traditional Chinese', nativeName: '繁體中文' },
  { code: 'cn', name: 'Simplified Chinese', nativeName: '簡體中文' },
];

interface Props {
  dropdownColor?: string;
  /** 'ghost' renders a compact pill with globe icon and transparent background */
  variant?: 'default' | 'ghost';
}

export const LanguageSelector: React.FC<Props> = ({ dropdownColor, variant = 'default' }) => {
  const { locale, setLocale, getCurrentLanguageCode } = useLocalization();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (languageCode: 'en' | 'ja' | 'cn' | 'tc') => {
    setLocale(languageCode);
    handleClose();
  };

  if (variant === 'ghost') {
    return (
      <>
        <Box
          onClick={handleClick}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'pointer',
            borderRadius: '20px',
            border: '1px solid rgba(0,0,0,0.14)',
            background: 'rgba(0,0,0,0.04)',
            px: '10px',
            py: '5px',
            transition: 'background 0.15s',
            '&:hover': { background: 'rgba(0,0,0,0.08)' },
          }}
        >
          <Language sx={{ fontSize: '14px', color: '#555' }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#444', letterSpacing: '0.3px' }}>
            {getCurrentLanguageCode()}
          </Typography>
          <KeyboardArrowDown sx={{ fontSize: '14px', color: '#666' }} />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{
            sx: {
              backgroundColor: 'white',
              borderRadius: '12px',
              mt: 0.5,
              minWidth: '140px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            },
          }}
        >
          {languages.map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageSelect(language.code)}
              selected={locale === language.code}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                py: 1,
                px: 2,
                backgroundColor: locale === language.code ? '#f5f5f5' : 'transparent',
                '&:hover': { backgroundColor: locale === language.code ? '#cfcfcfff' : '#eaeaeaff' },
                '&.Mui-selected': { backgroundColor: '#f5f5f5 !important' },
                '&.Mui-selected:hover': { backgroundColor: '#cfcfcfff !important' },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                {language.nativeName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
                {language.name}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          minWidth: '80px',
          border: dropdownColor ? `1px solid ${dropdownColor}` : 'none',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: '#000'
          }}
        >
          {getCurrentLanguageCode()}
        </Typography>
        <KeyboardArrowDown sx={{ fontSize: '20px', color: '#666' }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            borderRadius: '8px',
            mt: 1,
            minWidth: '120px',
          },
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageSelect(language.code)}
            selected={locale === language.code}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              py: 1,
              px: 2,
              backgroundColor: locale === language.code ? '#f5f5f5' : 'transparent',
              '&:hover': {
                backgroundColor: locale === language.code ? '#cfcfcfff' : '#eaeaeaff',
              },
              '&.Mui-selected': {
                backgroundColor: '#f5f5f5 !important',
              },
              '&.Mui-selected:hover': {
                backgroundColor: '#cfcfcfff !important',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
              {language.nativeName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
              {language.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};